/* eslint-disable import/no-cycle */
import React from 'react';
import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import ChevronRight from '@mui/icons-material/ChevronRightRounded';
import { format } from 'date-fns';
import { PropsOf } from '@emotion/react';
import { formatDollarAmount } from '../../utils/global/helper';
import TransactionBadge from './TransactionBadge';
import { Transaction } from '../../interfaces';
import ListItemSkeleton from './ListItemSkeleton';
import TransactionDetailModal from './TransactionDetailModal';
import Badge from '../global/Badge';

interface Props {
  transactions: Transaction[];
  isLoading?: boolean;
  modalEnabled?: boolean;
  showUnusedDepositBadge?: boolean;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  useSpecStyle?: boolean;
}

function TransactionButton(props: PropsOf<typeof Button>) {
  return <Button {...props} disableRipple />;
}

function TransactionsList({
  transactions,
  isLoading,
  modalEnabled = true,
  showUnusedDepositBadge = false,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  useSpecStyle = false,
}: Props) {
  const theme = useTheme();

  const [selectedTransaction, setSelectedTransaction] =
    React.useState<Transaction | null>(null);

  const isCardTx = (transaction: Transaction) =>
    ['CREDIT', 'TERMINAL'].includes(transaction.type);

  const IndividualTxComponent = modalEnabled ? TransactionButton : Box;

  const onClickTransaction = (transaction: Transaction) => {
    if (!modalEnabled) return;
    setSelectedTransaction(transaction);
  };

  const nonArchivedTransactions = transactions?.filter(
    transaction => transaction.status !== 'ARCHIVED',
  );

  return (
    <>
      <Stack spacing={0} width="100%">
        {!isLoading && (
          <>
            {nonArchivedTransactions?.map((transaction, index) => {
              const isFirst = index === 0;
              const isLast = index === transactions.length - 1;
              const isCard = isCardTx(transaction);
              const isDigital = transaction.type === 'DIGITAL';
              const isGiftCard = transaction.type === 'GIFT_CARD';
              return (
                <IndividualTxComponent
                  key={transaction.id}
                  onClick={() => onClickTransaction?.(transaction)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    width: '100%',
                    textAlign: 'left',
                    padding: 0,
                    margin: 0,
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                    borderBottom: !isLast
                      ? `1px solid ${theme.palette.lightGrey.main}`
                      : undefined,
                  }}>
                  <Box
                    width="100%"
                    py={isFirst ? 0 : 1.5}
                    pb={isFirst ? 1.5 : undefined}
                    key={transaction.id}>
                    <Typography
                      variant={useSpecStyle ? undefined : 'h5'}
                      color={useSpecStyle ? undefined : 'text.primary'}
                      sx={
                        useSpecStyle
                          ? {
                              fontSize: 'var(--sidebar-font-base)',
                              fontWeight: 700,
                              color: 'var(--sidebar-ink)',
                            }
                          : undefined
                      }>
                      {formatDollarAmount(transaction.amount)}{' '}
                      {transaction.amount === 0 &&
                      transaction.status === 'SUCCESS'
                        ? 'payment recorded'
                        : isCard
                        ? transaction.status === 'SUCCESS'
                          ? 'paid by card'
                          : transaction.status === 'FAILED' || transaction.error
                          ? 'payment failed'
                          : transaction.status === 'REFUNDED'
                          ? 'payment refunded'
                          : 'payment incomplete'
                        : isGiftCard
                        ? 'paid by gift card'
                        : isDigital
                        ? 'paid digitally (Zelle/Venmo/etc.)'
                        : 'paid in cash'}
                    </Typography>
                    <Typography
                      variant={useSpecStyle ? undefined : 'body2'}
                      color={useSpecStyle ? undefined : 'text.primary'}
                      sx={
                        useSpecStyle
                          ? {
                              fontSize: 'var(--sidebar-font-sm)',
                              color: 'var(--sidebar-secondary)',
                            }
                          : undefined
                      }>
                      {format(transaction.dateCreated, 'MMM d, yyyy')} at{' '}
                      {format(transaction.dateCreated, 'h:mm a')}{' '}
                      {transaction.tipAmount &&
                      (!isCard || transaction.status === 'SUCCESS')
                        ? `(incl. ${formatDollarAmount(
                            transaction.tipAmount,
                          )} tip)`
                        : null}
                      {transaction.chargeReason === 'reschedule'
                        ? `(rescheduling fee)`
                        : null}
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.5} mt={0.5}>
                      <TransactionBadge
                        transaction={transaction}
                        tooltipOffset={[0, -5]}
                        light={false}
                        sx={
                          useSpecStyle
                            ? {
                                height: 20,
                                padding: '0 9px',
                                borderRadius: '50px',
                              }
                            : undefined
                        }
                      />
                      {showUnusedDepositBadge &&
                        transaction.payment?.type === 'DEPOSIT' &&
                        transaction.payment?.depositStatus === 'UNUSED' && (
                          <Badge text="Unused Deposit" colorScheme="warning" />
                        )}
                    </Stack>
                  </Box>
                  {modalEnabled && (
                    <Box
                      height="100%"
                      display="flex"
                      alignItems="center"
                      color="text.tertiary">
                      <ChevronRight />
                    </Box>
                  )}
                </IndividualTxComponent>
              );
            })}

            {!transactions?.length && (
              <Typography variant="body2">No transactions to show</Typography>
            )}
          </>
        )}

        {isLoading && <ListItemSkeleton />}
      </Stack>
      {!isLoading && hasMore && onLoadMore && (
        <Button
          disableRipple
          disabled={isLoadingMore}
          onClick={onLoadMore}
          sx={{ marginTop: 1, alignSelf: 'flex-start', padding: 0 }}>
          {isLoadingMore ? 'Loading…' : 'Show more'}
        </Button>
      )}
      {modalEnabled && selectedTransaction && (
        <TransactionDetailModal
          onClose={() => setSelectedTransaction(null)}
          open={!!selectedTransaction}
          transaction={selectedTransaction}
        />
      )}
    </>
  );
}

export default TransactionsList;
